import React, { FC } from "react";

import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useMount } from "react-use";

import { PaginatedList } from "@/components/templates";
import { BrokerState, Claim, FeaturePageState, Permissions } from "@/shared/domain";
import { useAuth, useGlobalFilter, useNavigation, useRouterHook } from "@/shared/hooks";
import * as tagManager from "@/tagManager";
import { useUserFeedback } from "@/userFeedback";

import ClaimItem from "../components/ClaimItem/ClaimItem";
import ClaimTable from "../components/ClaimTable/ClaimTable";
import { useClaimsQueries } from "../useClaimsQueries";

const getAvailableFilters = (t: TFunction) => [
    { id: BrokerState.ALL, label: t("Common.All") },
    { id: BrokerState.ON_GOING, label: t("Common.InProgress") },
    { id: BrokerState.CLOSED, label: t("Common.Closed") },
];

const ClaimPage: FC = () => {
    const {
        state: { filterId },
    } = useRouterHook<FeaturePageState>();
    const { user } = useAuth();
    const { t } = useTranslation();
    const { incUsageCount } = useUserFeedback();
    const { navToFeatureDocuments, navToClaimSearch } = useNavigation();
    const { policyholdersGF } = useGlobalFilter();
    const { localFilters, useClaimsPageQuery, tableMode } = useClaimsQueries({ applyFilter: true });

    useMount(() => {
        incUsageCount();
        tagManager.pushViewItemListEvent("claim", "Claims");
    });
    const {
        page,
        setPage,
        orderBy,
        category,
        isFetching,
        handleSort,
        setCategory,
        totalOfPages,
        result: claims,
        handlePageSizeChange,
    } = useClaimsPageQuery({ defaultCategory: filterId || BrokerState.ALL });

    const renderItem = (claim) => <ClaimItem key={claim.key} claim={claim} />;

    const renderTable = (items: Claim[]) => <ClaimTable claims={items} orderBy={orderBy} handleSort={handleSort} />;

    return (
        <PaginatedList<Claim>
            page={page}
            tableMode={tableMode}
            localFilters={localFilters}
            emptyListMessage={t("Claims.Empty")}
            pageTitle={t("Common.Claims")}
            renderItem={renderItem}
            renderTable={renderTable}
            isFetchingList={isFetching}
            isLoading={isFetching}
            setPage={setPage}
            category={category}
            totalOfPages={totalOfPages}
            alwaysShowFilters={true}
            availableFilters={getAvailableFilters(t)}
            setCategory={setCategory}
            items={claims}
            handlePageSizeChange={handlePageSizeChange}
            openSearch={navToClaimSearch}
            openLinkedDocuments={() => navToFeatureDocuments("claims")}
            displayLinkedDocumentsButton={user.someHasPermission(Permissions.ViewLinkedDocuments, policyholdersGF)}
        />
    );
};

export default ClaimPage;
